import { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, fieldStyle, selectStyle, navyBtn } from '../../styles/tokens';
import client from '../../api/client';

const ROLES = [
  { key: 'supervisor', label: 'Supervisor(a)' },
  { key: 'ingeniero', label: 'Ingeniero(a)' },
  { key: 'gerente', label: 'Gerente' },
  { key: 'directora', label: 'Directora' },
  { key: 'administradora', label: 'Administradora' },
];

const PTO_TYPES = [
  { key: 'vacation', label: 'Vacaciones' },
  { key: 'sick', label: 'Enfermedad' },
  { key: 'personal', label: 'Personal' },
];

export default function PtoLimitsByRole() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [limits, setLimits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const yearOptions = [];
  for (let y = currentYear; y <= currentYear + 2; y++) yearOptions.push(y);

  useEffect(() => { fetchLimits(); }, [year]);

  async function fetchLimits() {
    setLoading(true);
    try {
      const res = await client.get(`/api/admin/pto-limits?year=${year}`);
      const map = {};
      ROLES.forEach(r => {
        map[r.key] = {};
        PTO_TYPES.forEach(t => { map[r.key][t.key] = 0; });
      });
      (res.data || []).forEach(row => {
        if (map[row.role_key]) {
          map[row.role_key][row.type] = parseFloat(row.total_days) || 0;
        }
      });
      setLimits(map);
    } catch (err) {
      console.error('Failed to load PTO limits:', err);
    } finally {
      setLoading(false);
    }
  }

  function updateLimit(roleKey, type, value) {
    setLimits(prev => ({
      ...prev,
      [roleKey]: { ...prev[roleKey], [type]: parseFloat(value) || 0 },
    }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const limitsArray = [];
      ROLES.forEach(r => {
        PTO_TYPES.forEach(t => {
          limitsArray.push({
            role_key: r.key,
            type: t.key,
            total_days: limits[r.key]?.[t.key] || 0,
          });
        });
      });
      await client.put('/api/admin/pto-limits', { year, limits: limitsArray });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar límites PTO');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando límites PTO…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>
          Límites PTO por Rol
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Año:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...selectStyle, width: 'auto' }}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleSave} disabled={saving} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {saved && (
        <div style={{
          marginBottom: 12, padding: '8px 14px', background: '#dcfce7', color: '#166534',
          border: '1px solid #86efac', borderRadius: 8, fontSize: 13,
        }}>
          Límites guardados. Los saldos PTO de empleados con estos roles han sido actualizados.
        </div>
      )}

      <div style={bubbleStyle}>
        {/* Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr repeat(3, 90px)',
          gap: 8, padding: '0 0 10px 0', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
        }}>
          <span>Rol</span>
          {PTO_TYPES.map(t => <span key={t.key} style={{ textAlign: 'center' }}>{t.label}</span>)}
        </div>

        {/* Rows */}
        {ROLES.map(role => (
          <div key={role.key} style={{
            display: 'grid', gridTemplateColumns: '1fr repeat(3, 90px)',
            gap: 8, padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{role.label}</span>
            {PTO_TYPES.map(t => (
              <input
                key={t.key}
                type="number"
                min={0}
                step={0.5}
                value={limits[role.key]?.[t.key] || ''}
                onChange={e => updateLimit(role.key, t.key, e.target.value)}
                style={{ ...fieldStyle, textAlign: 'center', padding: '5px 4px', fontSize: 13 }}
              />
            ))}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
        Al guardar, los saldos PTO de todos los empleados activos con el rol correspondiente se actualizarán automáticamente.
      </div>
    </div>
  );
}
